/* eslint-disable max-lines-per-function */
import type { ReactElement } from "react";
import type { ApiCollectionProviderItem, ApiTitle } from "@lumi/data";
import type { Variants } from "framer-motion";
import { HorizontalTitleCard } from "../../../shared/components/content-cards/HorizontalTitleCard";
import { ProviderCard } from "../../../shared/components/content-cards/ProviderCard";
import { SkeletonHorizontalTitleCard } from "../../../shared/components/content-cards/SkeletonHorizontalTitleCard";
import { SkeletonProviderCard } from "../../../shared/components/content-cards/SkeletonProviderCard";
import { SkeletonTitleCard } from "../../../shared/components/content-cards/SkeletonTitleCard";
import { TitleCard } from "../../../shared/components/content-cards/TitleCard";
import { RAIL_DATA_ATTR, RAIL_ITEM_DATA_ATTR } from "../../../shared/constants/navigation";
import { useRailNavigation } from "../../../shared/hooks";

/**
 * Reusable horizontal rail component for displaying title or provider collections
 * @example
 * ```tsx
 * // Title collection
 * <CollectionRail title="New Releases" items={titles} transitionTo={transitionTo} />
 *
 * // Provider collection
 * <CollectionRail title="Content Partners" providers={providers} cardType="SQUARE" transitionTo={transitionTo} />
 * ```
 */

interface CollectionRailProps {
  /** Display title for the rail */
  readonly title: string;
  /** Array of titles to display (for VERTICAL/HORIZONTAL card types) */
  readonly items?: ApiTitle[];
  /** Array of providers to display (for SQUARE card type / PROVIDER template) */
  readonly providers?: ApiCollectionProviderItem[];
  /** Card display type (VERTICAL, HORIZONTAL, or SQUARE for providers) */
  readonly cardType?: "VERTICAL" | "HORIZONTAL" | "SQUARE";
  /** Whether to show a loading skeleton */
  readonly loading?: boolean;
  /** Navigation function with exit animation support */
  readonly transitionTo: (path: string, variant?: Variants) => void;
  /** Hide action buttons on cards (for browse-only contexts like Title page Suggested tab) */
  readonly hideActions?: boolean;
  /** When true, Enter navigates to info (title page) instead of playing. Used with hideActions for browse-only rails. */
  readonly preferInfoOnEnter?: boolean;
  /** Hide details (title, year, etc.) beneath the card image */
  readonly hideDetails?: boolean;
}

export function CollectionRail({
  title,
  items = [],
  providers = [],
  cardType = "VERTICAL",
  loading = false,
  transitionTo,
  hideActions = false,
  preferInfoOnEnter = false,
  hideDetails = false,
}: CollectionRailProps): ReactElement | null {
  // Determine which items to use for navigation based on card type
  const isProviderRail = cardType === "SQUARE";
  const itemCount = isProviderRail ? providers.length : items.length;

  const { railRef, handleRailFocus, handleRailBlur, handleRailKeyDown, getCardProps, registerWatchlistHandler } =
    useRailNavigation({
      itemCount,
      preferInfoOnEnter,
      onAction: (index) => {
        if (isProviderRail) {
          const provider = providers[index];
          if (!provider) return;
          transitionTo(`/providers/${provider.provider.slug}`);
        } else {
          const item = items[index];
          if (!item) return;
          transitionTo(`/home/watch/${item.slug}`);
        }
      },
      onInfo: (index) => {
        if (isProviderRail) {
          const provider = providers[index];
          if (!provider) return;
          transitionTo(`/providers/${provider.provider.slug}`);
        } else {
          const item = items[index];
          if (!item) return;
          transitionTo(`/home/title/${item.slug}`);
        }
      },
    });

  // Don't render empty rails
  if (!loading && itemCount === 0) {
    return null;
  }

  // Prevent rail from receiving focus on click - only allow keyboard focus
  const handlePointerDown = (e: React.PointerEvent) => {
    // Prevent the rail from receiving focus when clicked
    // Focus should only happen via Tab key navigation
    e.preventDefault();
  };

  return (
    <section className="space-y-4" aria-label={title}>
      <div className="container mx-1 px-8">
        <h2 className="text-2xl font-bold text-white">{title}</h2>
      </div>
      {/* 
                Semantic HTML: Use <ul> instead of <div role="list"> for better accessibility.
                The roving tabindex pattern (tabIndex={0} + keyboard handlers) is a valid
                accessibility technique used by major platforms (Google, Apple, Microsoft).
                SonarLint warnings about "non-interactive element" are false positives - this
                element IS interactive via keyboard. CSS resets remove default list styling.
            */}
      {}
      <ul //NOSONAR
        ref={railRef as React.RefObject<HTMLUListElement>}
        tabIndex={0} //NOSONAR
        {...{ [RAIL_DATA_ATTR]: "" }}
        aria-label={`${title} content rail - use arrow keys to navigate`}
        className="relative -my-20 focus:outline-none"
        onFocus={handleRailFocus}
        onBlur={handleRailBlur}
        onKeyDown={handleRailKeyDown}
        onPointerDown={handlePointerDown}
      >
        <div
          className="
                        flex gap-6 overflow-x-auto py-12 px-8 -my-12
                        [-ms-overflow-style:none] [scrollbar-width:none]
                        [&::-webkit-scrollbar]:hidden snap-x snap-mandatory
                    "
        >
          {/* Loading skeletons */}
          {loading &&
            cardType === "HORIZONTAL" &&
            Array.from({ length: 8 }, (_, i) => <SkeletonHorizontalTitleCard key={`loading-${title}-${i}`} />)}
          {loading &&
            cardType === "VERTICAL" &&
            Array.from({ length: 8 }, (_, i) => <SkeletonTitleCard key={`loading-${title}-${i}`} />)}
          {loading &&
            cardType === "SQUARE" &&
            Array.from({ length: 8 }, (_, i) => <SkeletonProviderCard key={`loading-${title}-${i}`} />)}

          {/* Horizontal title cards */}
          {!loading &&
            cardType === "HORIZONTAL" &&
            items.map((t, index) => {
              const cardProps = getCardProps(index);
              return (
                <li key={t.id} ref={cardProps.ref} {...{ [RAIL_ITEM_DATA_ATTR]: "" }}>
                  <HorizontalTitleCard
                    title={t}
                    index={index}
                    className={cardProps.className}
                    transitionTo={transitionTo}
                    registerWatchlistHandler={registerWatchlistHandler}
                    hideActions={hideActions}
                    hideDetails={hideDetails}
                  />
                </li>
              );
            })}

          {/* Vertical title cards */}
          {!loading &&
            cardType === "VERTICAL" &&
            items.map((t, index) => {
              const cardProps = getCardProps(index);
              return (
                <li key={t.id} ref={cardProps.ref} {...{ [RAIL_ITEM_DATA_ATTR]: "" }}>
                  <TitleCard
                    title={t}
                    index={index}
                    className={cardProps.className}
                    transitionTo={transitionTo}
                    hideActions={hideActions}
                  />
                </li>
              );
            })}

          {/* Square provider cards */}
          {!loading &&
            cardType === "SQUARE" &&
            providers.map((item, index) => {
              const cardProps = getCardProps(index);
              return (
                <li key={item.provider.id} ref={cardProps.ref} {...{ [RAIL_ITEM_DATA_ATTR]: "" }}>
                  <ProviderCard
                    provider={item.provider}
                    index={index}
                    className={cardProps.className}
                    transitionTo={transitionTo}
                  />
                </li>
              );
            })}
        </div>
      </ul>
    </section>
  );
}
